import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { Phone, MessageSquare, Search, ChevronDown } from "lucide-react";

export default function FaqPage() {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/faq").then((r) => setFaqs(r.data));
  }, []);

  const filtered = faqs.filter((f) => !q || f.question.toLowerCase().includes(q.toLowerCase()) || f.answer.toLowerCase().includes(q.toLowerCase()));
  const byCategory = filtered.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <header className="sticky top-0 bg-white border-b-2 border-asap-deep z-30">
        <div className="max-w-5xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/"><Logo size={64} compact /></Link>
          <Link to="/" className="px-4 py-2 border-2 border-asap-deep font-bold uppercase text-sm hover:bg-asap-deep hover:text-white">← Home</Link>
        </div>
      </header>

      <section className="bg-gradient-to-br from-asap-deep to-asap-blue text-white py-14">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-xs tracking-[0.3em] uppercase text-asap-yellow font-bold">Help Center</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase mt-2">How can we help?</h1>
          <div className="mt-6 flex items-center bg-white text-asap-deep border-2 border-asap-yellow px-4 py-3 max-w-2xl">
            <Search size={20} className="text-asap-orange"/>
            <input data-testid="faq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="ml-3 flex-1 outline-none font-bold"/>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_280px] gap-10">
        <div className="space-y-10">
          {Object.entries(byCategory).map(([cat, items]) => (
            <section key={cat}>
              <div className="text-xs uppercase tracking-[0.3em] text-asap-orange font-bold">{cat}</div>
              <div className="mt-3 border-2 border-asap-deep divide-y-2 divide-asap-deep">
                {items.map((f) => (
                  <div key={f.id} data-testid={`faq-item-${f.id}`}>
                    <button
                      onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))}
                      className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-asap-yellow/10"
                    >
                      <span className="font-bold text-asap-deep">{f.question}</span>
                      <ChevronDown size={18} className={`transition-transform ${open[f.id] ? "rotate-180" : ""}`}/>
                    </button>
                    {open[f.id] && <div className="px-5 pb-4 text-slate-700">{f.answer}</div>}
                  </div>
                ))}
              </div>
            </section>
          ))}
          {filtered.length === 0 && <div className="text-slate-400">No articles matched "{q}"</div>}
        </div>

        <aside className="lg:sticky lg:top-28 self-start space-y-4">
          <div className="bg-asap-yellow/20 border-2 border-asap-yellow p-5">
            <div className="text-xs uppercase tracking-[0.3em] text-asap-orange font-bold">Still stuck?</div>
            <h3 className="font-display text-xl uppercase text-asap-deep mt-1">Talk to a human</h3>
            <a href="tel:+18317477141" data-testid="support-call" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-asap-deep text-asap-yellow font-display uppercase border-2 border-asap-deep hover:bg-asap-blue">
              <Phone size={16}/> 831-747-7141
            </a>
            <a href="sms:+17653301951" className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-3 border-2 border-asap-deep text-asap-deep font-display uppercase hover:bg-asap-deep hover:text-white">
              <MessageSquare size={16}/> Text 765-330-1951
            </a>
            <a href="mailto:asaproadrunners@rs-asap.com" className="mt-2 block text-sm text-center text-asap-deep underline">asaproadrunners@rs-asap.com</a>
          </div>
          <div className="border-2 border-asap-deep p-4 text-xs text-slate-600">
            Logged in? Open a ticket from your dashboard for faster routing to the right team.
          </div>
        </aside>
      </main>
    </div>
  );
}
